/**
 * ステージ（都道府県 × モード）コンテンツの型定義。
 *
 * ゲームロジックはこの型にのみ依存し、「福岡」固有の名称を直接参照しない。
 * 新しい都道府県を追加する場合は StageDefinition を1つ追加するだけで済む。
 */

/** ステージのモード。将来 observatory(観光名所) / person(歴史人物) を追加する。 */
export type StageMode = 'food' | 'sightseeing' | 'person';

/**
 * 背景情景の時間帯。
 * 到達レベルが上がるにつれて夕暮れ→薄暮→夜→深夜→夜明けへ移り、
 * 「屋台で過ごす一夜」が進んでいく演出に使う（進捗の可視化）。
 */
export type ScenePhase = 'dusk' | 'evening' | 'night' | 'deepnight' | 'dawn';

/**
 * 仮アイコン（画像未配置時）の描画モチーフ。
 * 色だけに依存しないため、各アイテムで形を変えて識別性を確保する。
 */
export type IconShape =
  | 'berry'
  | 'mochi'
  | 'roe'
  | 'rice-bowl'
  | 'noodle-bowl'
  | 'dumpling'
  | 'curry-plate'
  | 'fish'
  | 'ramen'
  | 'pot'
  | 'hotpot'
  | 'feast'
  | 'sashimi-platter';

/** 仮アイコンの見た目定義（アクセシビリティのため文字＋形＋レベルを併用）。 */
export interface IconStyle {
  /** 円の中央に描く短縮文字（例: 苺 / 餃子 / 豚骨）。 */
  label: string;
  /** 仮アイコンの形。 */
  shape: IconShape;
  /** 主色。 */
  baseColor: string;
  /** 差し色。 */
  accentColor: string;
  /** 文字色（省略時は自動判定）。 */
  textColor?: string;
}

/**
 * 地域名から背景ツアーなどに使う短い呼び名を作る。
 *
 * 「福岡県」→「福岡」、「大阪府」→「大阪」、「東京都」→「東京」。
 * **「北海道」は道を落とさない**（落とすと「北海」になってしまう）。
 * 都道府県でないご当地名（庄内など）はそのまま返す。
 */
export function regionShortName(regionName: string): string {
  return regionName.replace(/[都府県]$/, '');
}

/** 輪郭の頂点列（[x, y] の配列）。 */
export type ItemHull = ReadonlyArray<readonly [number, number]>;

/** 1アイテム（料理 / 名所 / 人物）の定義。 */
export interface StageItemDefinition {
  /** 一意なID（英小文字とハイフン推奨）。 */
  id: string;
  /** 1 から始まる連番のレベル。 */
  level: number;
  /** 表示名。 */
  name: string;
  /** 一覧表示用の短い名前（省略時は name）。 */
  shortName?: string;
  /**
   * 本番画像のパス（PNG / WebP）。
   * ファイルが存在しない場合は自動的に仮アイコンへフォールバックする。
   */
  image?: string;
  /**
   * 実画像の輪郭（凸多角形）。画像全体を -1..1 に正規化した座標。
   * 指定があり、実画像が読み込めた場合はこの形の物理ボディになる（無ければ円）。
   * 通常は scripts/process-food-images.mjs が自動生成する。
   */
  hull?: ItemHull;
  /** 物理ボディの大きさの基準（論理座標 480x720 基準）。輪郭がある場合も面積はこの円と同じになる。 */
  radius: number;
  /** 物理ボディの質量。 */
  mass: number;
  /** このアイテムが「生成されたとき」の基本得点。 */
  score: number;
  /** 仮アイコンの描画設定。 */
  icon: IconStyle;
  /** 補足説明（図鑑ツールチップ用、任意）。 */
  description?: string;
  /**
   * 選定の出典URL（任意）。
   *
   * 記者の裏取りコストを下げるために持つ。農林水産省「うちの郷土料理」や
   * 自治体・公式観光協会のページを想定。未設定でも動くが validate が警告する。
   */
  source?: string;
  /** 累計スコアで解放される「食べに行く」導線（任意）。 */
  reward?: ItemReward;
}

/** ごほうび（お店案内）の外部リンク1件。 */
export interface RewardLink {
  /** ボタンの表示名（例: 地図で探す）。 */
  label: string;
  /** 遷移先 URL。 */
  url: string;
}

/**
 * 「食べに行く」導線。
 *
 * その料理を作ったことがあり、かつ累計スコアが `unlockScore` を超えると
 * 進化一覧のタイルから開けるようになる。
 *
 * 既定では実在店舗を断定せず「エリア × 料理名」の検索リンクにしている。
 * 提携店が決まったら `links[].url` を実店舗のURLへ差し替えるだけで送客できる。
 */
export interface ItemReward {
  /** 解放に必要な累計スコア。 */
  unlockScore: number;
  /** おすすめエリア（例: 中洲・川端）。 */
  area: string;
  /** ひとこと紹介。 */
  headline: string;
  /** 食べ方のコツなど（任意）。 */
  tip?: string;
  links: RewardLink[];
  /**
   * 県・市町村・観光協会の公式ページ（任意）。
   * あれば「公式」の印を付けて、検索リンクより上に出す。
   * 自治体タイアップのとき、ここがそのまま「公式の送客枠」になる。
   */
  official?: RewardLink[];
}

/** テーマカラー（CSS変数へ流し込まれる）。 */
export interface StageTheme {
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  backgroundColor: string;
}

/** 抽選テーブルの1行。 */
export interface SpawnWeight {
  level: number;
  /** 重み（％表記を推奨。合計100になるよう検証する）。 */
  weight: number;
}

/**
 * 効果音・BGMのパス（ファイルが無くても動作する）。
 * 未配置のキーは WebAudio による合成音へ自動フォールバックする。
 * 全県共通の既定は config/audio.ts の DEFAULT_AUDIO_PATHS。ここは県ごとに変えたいものだけ書く。
 */
export interface StageAudioPaths {
  bgm?: string;
  drop?: string;
  merge?: string;
  combo?: string;
  /** 最上位（Lv12）ができたとき。 */
  top?: string;
  gameOver?: string;
  bonus?: string;
  unlock?: string;
}

/** ステージ定義本体。 */
export interface StageDefinition {
  /**
   * 地域ID。例: 'fukuoka' / 'hokkaido' / 'shonai'（庄内）。
   *
   * 都道府県とは限らない。市町村や食文化圏の「ご当地ステージ」もここに入るため、
   * prefecture ではなく region という名前にしている。
   */
  regionId: string;
  /** 例: '福岡県' / '北海道' / '庄内'。 */
  regionName: string;
  /**
   * 親ステージのID（任意）。ご当地ステージを都道府県にぶら下げるのに使う。
   *
   * 例: 庄内めし（regionId: 'shonai'）は parentId: 'yamagata-food'。
   * 全国の制覇率は親を持たないステージだけで数えるので、ご当地を足しても分母が壊れない。
   */
  parentId?: string;
  mode: StageMode;
  /**
   * 制作途中のステージ（任意）。
   *
   * データは先に作るが、絵がまだ揃っていない県はこれを立てる。
   * ステージ選択には出さず、制覇率の分母にも入れない。
   * ただし `/hokkaido` のような直リンクでは開けるので、自分で下見できる。
   *
   * 47県ぶんを一気に完成させることはないので、
   * 「データはあるが人には見せない」状態を持てるようにしている。
   */
  draft?: boolean;
  /** ゲームタイトル（画面表示・document.title に使用）。 */
  title: string;
  /** サブタイトル（任意）。 */
  subtitle?: string;
  theme: StageTheme;
  /** 抽選テーブル（省略時は GAME_TUNING.spawn.defaultWeights）。 */
  spawnWeights?: SpawnWeight[];
  /** レベル昇順のアイテム配列。 */
  items: StageItemDefinition[];
  audio?: StageAudioPaths;
  /** 背景情景の設定（写真を使う場合の差し込み口）。 */
  scenery?: StageScenery;
}

/**
 * 背景情景の設定。
 *
 * 写真（JPEG / WebP）を置けば実写背景に切り替わり、置かなければ
 * 手続き生成の夜景へ自動フォールバックする。権利的に安全な素材だけを置くこと。
 */
export interface StageScenery {
  /** 時間帯ごとの背景写真。存在しないフェーズは手続き生成のままになる。 */
  photos?: Partial<Record<ScenePhase, string>>;
  /** 写真のクレジット表記（任意。画面右下に小さく出す）。 */
  credit?: string;
  /** 「福岡めぐり」のような時間で切り替わる背景ツアー（任意）。 */
  tour?: StageTour;
}

/** 時間で切り替わる背景ツアーの定義。 */
export interface StageTour {
  /** ツアー開始時に並べる景色の配列（順番に表示）。 */
  scenes: TourScene[];
}

/** 背景ツアーの1景。 */
export interface TourScene {
  /** 一意なID（英小文字とハイフン推奨）。 */
  id: string;
  /** 看板の地名。 */
  name: string;
  /** 看板の小見出し（市町村・時間帯）。 */
  area: string;
  /** 背景画像のパス。 */
  image: string;
  /** 画像内のホットスポット（豆知識マーカー）。 */
  hotspots: TourHotspot[];
  /**
   * 追悼・慰霊・聖域など、静かに扱うべき景色。
   *
   * true のあいだは、祝祭的な演出を止める。
   *  - 空の花火・流れ星を上げない
   *  - 合体時の画面振動を起こさない
   *
   * 原爆ドーム、戦没者を扱う施設、いまも祈りの場である御嶽などに付ける。
   * 迷ったら付ける。止めて困る演出ではない。
   */
  solemn?: boolean;
}

/** 背景ツアーのホットスポット（豆知識マーカー）。 */
export interface TourHotspot {
  /** 一意なID。発見記録の保存キーになる。 */
  id: string;
  /** 画像内の正規化 X 座標（0〜1）。 */
  x: number;
  /** 画像内の正規化 Y 座標（0〜1）。 */
  y: number;
  /** マーカーの見出し（スポット名）。 */
  title: string;
  /** 豆知識の本文（2〜3文）。 */
  body: string;
  /** 関連料理のID（任意）。 */
  relatedItemId?: string;
  /** Google マップの検索語。 */
  mapQuery: string;
  /**
   * 豆知識の出典URL（任意）。
   *
   * 国の機関・自治体・公式観光協会のページを想定。
   * 未設定でも動くが validate が警告する。
   */
  source?: string;
}

/** ステージ検証の結果。 */
export interface StageValidationResult {
  ok: boolean;
  errors: string[];
  warnings: string[];
}
